import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled, { keyframes } from "styled-components";

import { CloseButton as LicenseCloseButton } from "@/components/LicenseModal/styles";
import LicenceDocument from "@/components/LicenceDocument";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import { getClaimCertificate, LicenceBlock, LicenceCertificate, openLicencePdfTab } from "@/services/licences.service";
import { colors, components, fonts, fontSizes } from "@/styles/design-system";
import { bannerGradient } from "@/utils/bannerGradient";

/**
 * The YouTube Licence certificate for one resolved claim.
 *
 * Read-only: it opens a document, it doesn't ask for anything. The text comes
 * rendered from the backend as blocks — the same blocks the PDF is drawn from —
 * so what is read here and what is downloaded can never say different things.
 */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  height: 100dvh;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.55);
`;

const Container = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 760px;
  max-height: 88dvh;
  display: flex;
  flex-direction: column;
  background: ${components.modal.background};
  border: ${components.modal.border};
  border-radius: ${components.modal.borderRadius};
  box-shadow: ${components.modal.boxShadow};
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;

  @media (max-width: 600px) {
    width: 100%;
    max-height: 92dvh;
  }
`;

/* The positioning context for the two controls. The heading and the licence
   number stack at every width — side by side, the number read as part of the
   title rather than as the document's reference. */
const Header = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 22px 30px 18px;
  border-bottom: 1px solid rgba(235, 194, 142, 0.16);

  @media (max-width: 600px) {
    padding: 18px 18px 14px;
  }
`;

/* Only the title has to clear the two controls; the code line below doesn't. */
const TitleBlock = styled.div`
  min-width: 0;
  padding-right: 160px;

  @media (max-width: 600px) {
    padding-right: 140px;
  }
`;

const Title = styled.h2`
  font-family: ${fonts.heading};
  font-size: 1.45rem;
  font-weight: 500;
  line-height: 1.15;
  color: ${colors.text.heading};
  margin: 0 0 6px;

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const CodeLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  /* Phone: its own full-width row so the code never wraps. The copy button
     stays next to the code it copies rather than being pushed to the far edge,
     where it reads as belonging to the row instead of to the number. */
  @media (max-width: 600px) {
    width: 100%;
  }
  font-family: ${fonts.number};
  font-size: 0.85rem;
  letter-spacing: 0.6px;
  color: ${colors.text.link};
`;

/* Vertically centred in the header, and on the same line as the ✕. */
const HeaderActions = styled.div`
  position: absolute;
  /* On the heading's line, not the header's midpoint: the header is two rows
     tall, so "centred" would drop the controls between them. */
  top: 18px;
  right: 60px;

  @media (max-width: 600px) {
    top: 15px;
  }
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
`;

/* The shared gold-circle ✕, moved off the corner onto the same centre line. */
const CloseButton = styled(LicenseCloseButton)`
  top: 18px;

  @media (max-width: 600px) {
    top: 15px;
  }
`;

/* Copy the licence number — it's the reference a customer quotes at support,
   and nobody should have to retype it off the screen. */
const CopyBtn = styled.button<{ $copied?: boolean }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  color: ${({ $copied }) => ($copied ? "#9ad3a8" : "rgba(251, 244, 236, 0.55)")};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #fbf4ec;
    background: rgba(235, 194, 142, 0.14);
  }

  svg {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const IconBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 600;
  color: #fff;
  background: rgba(235, 194, 142, 0.1);
  border: 1px solid rgba(235, 194, 142, 0.28);
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: rgba(235, 194, 142, 0.2);
    transform: translateY(-1px);
  }
`;

/* Same scrollbar as the LicenseModal family — gold thumb, stable gutter. */
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(235, 194, 142, 0.6) transparent;

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.55);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.75);
    background-clip: padding-box;
  }

  padding: 22px 30px 30px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  line-height: 1.62;
  color: ${colors.text.body};

  @media (max-width: 600px) {
    padding: 18px 18px 24px;
  }
`;

/* The gap between sections lives HERE, on the wrapper, not on the heading.
   Each section holds exactly one heading, so a `:first-of-type` rule on the
   heading matched every one of them and zeroed the gap everywhere. */
/* The banners' red, said the same way they say it — the tuned gradient from
   `bannerGradient`, lighter at the start, the true colour in the middle, deeper
   at the end. Deliberately WITHOUT the hex texture the global banners carry:
   this sits inside a document, where a pattern behind the words would fight the
   text rather than mark a strip across the page. */
const VoidBanner = styled.div`
  margin: 0 0 16px;
  padding: 12px 15px;
  border-radius: 10px;
  background: ${bannerGradient("rgb(195, 75, 54)")};
  color: #fdf3f0;
  font-size: ${fontSizes.sm};
  line-height: 1.45;

  strong {
    color: #ffffff;
  }
`;

const LicenceCertificateModal: React.FC<{
  claimId: string | null;
  onClose: () => void;
}> = ({ claimId, onClose }) => {
  const { mounted, closing } = useModalAnimation(!!claimId);
  const [certificate, setCertificate] = useState<LicenceCertificate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!claimId) return;
    let cancelled = false;
    setCertificate(null);
    setError(null);
    getClaimCertificate(claimId)
      .then((data) => { if (!cancelled) setCertificate(data); })
      .catch(() => { if (!cancelled) setError("This licence could not be loaded. Please try again, or contact support."); });
    return () => { cancelled = true; };
  }, [claimId]);

  // Reset the scroll position between certificates — otherwise the second one
  // opens halfway down the first one's prohibitions.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [certificate]);

  const [copied, setCopied] = useState(false);
  const copyCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }, []);

  const openPdf = useCallback(() => {
    if (!claimId) return;
    void openLicencePdfTab(claimId);
  }, [claimId]);

  if (!mounted) return null;

  const isVoid = certificate?.licence.status === "void";

  return createPortal(
    <Overlay>
      <Container $closing={closing}>
        <Header>
          <TitleBlock>
            <Title>{certificate?.rendered.title.replace(/^Audio Sanctum — /, "") ?? "YouTube Licence Agreement"}</Title>
          </TitleBlock>
            {certificate && (
              <CodeLine>
                License: {certificate.licence.licenceCode}
                <CopyBtn
                  $copied={copied}
                  onClick={() => copyCode(certificate.licence.licenceCode)}
                  aria-label={copied ? "Copied" : "Copy licence number"}
                >
                  {copied ? (
                    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </CopyBtn>
              </CodeLine>
            )}

          {/* Both controls live INSIDE the header — absolutely positioned
              against it, so "centred" means centred on the header band and not
              on the whole modal. */}
          <HeaderActions>
            {certificate && (
              <IconBtn onClick={openPdf}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                PDF
              </IconBtn>
            )}
          </HeaderActions>

          {/* The canonical gold-circle ✕ every modal in this family uses. */}
          <CloseButton onClick={onClose} aria-label="Close">
            &#x2715;
          </CloseButton>
        </Header>

        <Body ref={bodyRef}>
          {error && <VoidBanner>{error}</VoidBanner>}
          {!error && !certificate && (
            <div style={{ color: colors.text.muted, padding: "20px 0" }}>Loading your licence…</div>
          )}
          {isVoid && (
            <VoidBanner>
              <strong>This licence has been voided.</strong>{" "}
              {certificate?.licence.voidReason || "The claim on this video was re-asserted."}
            </VoidBanner>
          )}
          {certificate && <LicenceDocument sections={certificate.rendered.sections} />}
        </Body>
      </Container>
    </Overlay>,
    document.body,
  );
};

export default LicenceCertificateModal;
